import { request } from './client'
/* ⚠️ Type-only, and the direction every other module in here uses — `staff.ts`
   imports the same shape from the same place. */
import type { StaffAuth } from './teaching'
import type { AgeBand, Role } from '../types/prisma-enums'
import type { StaffTokens } from '../stores/staffSession'
import type { ParentTokens } from '../stores/parentSession'

/**
 * `POST /auth/device` — api.md §2, and the only public endpoint the child's
 * app calls — plus §10.0's staff sign-in.
 */

export interface DeviceProfile {
  /** The child's given name. Personal data — see the note in device.ts. */
  name: string
  ageBand: AgeBand
  /** A short avatar slug, never a URL (§4.1's DTO rejects one). */
  avatar?: string
  classId?: string
}

export interface DeviceRegistration {
  playerId: string
  deviceToken: string
}

/**
 * 🔴 IDEMPOTENT SERVER-SIDE, AND NON-MUTATING WITH IT.
 *
 * The same `deviceId` returns the same `Player` — and §4.1 deliberately does
 * **not** update `name`/`ageBand` on re-registration, because `deviceId` is a
 * bearer credential on a public endpoint and an upsert would let anyone who
 * learned one rename a child on their teacher's dashboard.
 *
 * The consequence for this side of the wire is the one that matters: **whatever
 * name is sent first is permanent.** Never call this with a placeholder (see
 * OD-40 and device.ts).
 */
export function registerDevice(
  deviceId: string,
  profile: DeviceProfile,
): Promise<DeviceRegistration> {
  return request<DeviceRegistration>('/auth/device', {
    method: 'POST',
    // Public: no token. The device has none yet, and this is the call that
    // issues one.
    context: 'public',
    body: {
      deviceId,
      name: profile.name,
      ageBand: profile.ageBand,
      ...(profile.avatar ? { avatar: profile.avatar } : {}),
      ...(profile.classId ? { classId: profile.classId } : {}),
    },
  })
}

/* ─────────────────────────────────────────── OD-52 · child sign-in ── */

/** Exactly what `POST /auth/kid` returns. */
export interface KidSession {
  playerId: string
  deviceToken: string
  name: string
  avatar: string | null
  ageBand: AgeBand
  classId: string
  className: string
  /** So the tablet can label the class card it remembers — see `knownClasses.ts`. */
  schoolName: string
}

/**
 * The one credential the child sends, and it is one or the other.
 *
 * 🔴 A UNION RATHER THAN TWO OPTIONAL FIELDS, BECAUSE THE SERVER REFUSES BOTH.
 * `kidLoginSchema` requires exactly one — sending both would buy two guesses for
 * one slot of the per-child delay — and a type with two optionals would let this
 * client build that body and discover the rule as a 400 in front of a child.
 */
export type KidCredential = { password: string } | { name: string }

/**
 * `POST /auth/kid` — class code + roll number + one credential.
 *
 * 🔴 THIS IS NOT `registerDevice` AND MUST NOT BE CONFUSED WITH IT. That call
 * creates a blank child on a tablet nobody has rostered; this one signs a child
 * into a roster row a teacher already made. `play.prisma`'s note on `deviceId`
 * explains why `/auth/device` may not do this itself: knowing a roll number is
 * *naming* a child, and a roll number is written on a school bag. The password
 * is what turns naming into proving.
 *
 * 🔴 AND FOR THE 3–6 BAND THE CREDENTIAL IS THE CHILD'S OWN NAME. A password is
 * unreachable at that age; a name copied off a name tag is letter-matching. The
 * server allows it only for a child who has no play password set, so it can
 * never downgrade one that exists — `kid-auth.service.ts` carries the full rule
 * and the trade it makes.
 *
 * ⚠️ THE FAILURE IS ONE GENERIC 401 AND THE SCREEN MUST KEEP IT THAT WAY. The
 * server answers identically for an unknown class code, an archived class, a
 * suspended school, an unknown roll number, a wrong password and a wrong name —
 * telling them apart would hand an unauthenticated caller a roster of identified
 * children. `client.ts` discards the body, so the only honest sentence names the
 * things a child can act on.
 *
 * ⚠️ IT ALSO ANSWERS 429, AND A CLASSROOM WILL MEET IT. Five a minute per IP,
 * and twenty children signing in at 9:00 share one school connection — so the
 * screen must distinguish that from a wrong password, because they need
 * opposite actions. And 403 for a device an admin revoked (S2), which needs a
 * third: nothing the child types will fix it.
 */
export function kidLogin(
  deviceId: string,
  credentials: { classCode: string; rollNumber: string } & KidCredential,
): Promise<KidSession> {
  const { classCode, rollNumber, ...credential } = credentials
  return request<KidSession>('/auth/kid', {
    method: 'POST',
    // Public: this is the call that ISSUES the device token, so sending one
    // would put a credential in the access log of a route that never reads it.
    context: 'public',
    /*
     * ⚠️ THE CREDENTIAL IS SPREAD RATHER THAN NAMED, so exactly the one key the
     * caller supplied is sent. Writing `password: …, name: …` here would put a
     * `undefined` beside the real one, and `JSON.stringify` drops those — which
     * works, silently, until the day something serialises differently.
     */
    body: { deviceId, classCode, rollNumber, ...credential },
  })
}

/* ────────────────────────────────────────── §10.0 · staff sign-in ── */

/**
 * `POST /auth/login` — the route four units of screens have been unreachable
 * behind.
 *
 * 🔴 `context: 'public'`, AND THAT IS NOT AN OVERSIGHT. This is the call that
 * *issues* the credential; sending one is how a credential ends up in the access
 * log of a route that never reads it (`client.ts`'s rule for `/auth/device`,
 * unchanged here).
 *
 * ⚠️ THE FAILURE IS ONE GENERIC 401 AND THE SCREEN MUST KEEP IT THAT WAY. The
 * server answers identically for an unknown email, a wrong password, a
 * deactivated account and a parent — *"a form that distinguishes them is a
 * directory of a school's staff"* (`auth.controller.ts`). `client.ts` already
 * discards the response body, so the only thing a caller has is the status, and
 * the only honest sentence is "that email and password did not match".
 *
 * ⚠️ IT ALSO ANSWERS 429. Five a minute per IP, and a school where six teachers
 * sign in at 9:00 from one connection will meet it — so the screen distinguishes
 * *that* from a wrong password, because they need opposite actions.
 */
/**
 * 🔴 A SECOND ANSWER, AND THE TYPE FORCES THE CALLER TO NOTICE IT.
 *
 * An account with an authenticator gets `{ mfaRequired: true, mfaToken, … }` and
 * **no tokens at all**. The union is discriminated rather than
 * `StaffTokens & { mfaRequired?: boolean }` on purpose: the second shape lets a
 * caller pass a half-authenticated response to `setStaffSession`, which would
 * store two empty strings as a session and 401 on every call afterwards for
 * reasons nobody could trace.
 */
export interface MfaChallenge {
  mfaRequired: true
  /** Purpose-scoped and five minutes. It grants nothing — see the server's note. */
  mfaToken: string
  expiresInSeconds: number
}

/**
 * 🔴 THE THIRD ANSWER: *"correct password, and this account has no second factor
 * yet."*
 *
 * A second factor is compulsory for operators, so signing in has to be able to
 * hand out the first one. This carries a fifteen-minute token worth exactly what
 * the password already proved — it can create a factor on an account that has
 * none, and nothing else.
 */
export interface MfaEnrolmentChallenge {
  mfaEnrolmentRequired: true
  enrolmentToken: string
  expiresInSeconds: number
}

/**
 * 🔴 THE FOURTH ANSWER: *"correct password — and somebody else chose it."*
 *
 * `User.mustChangePassword` is set when an admin typed a teacher's password or
 * Kalqy issued a temporary one (D10/D11). No session exists yet: the token is
 * purpose-scoped and spendable only on `POST /auth/password/change-required`,
 * which then continues the sign-in exactly where this left off (tokens, or the
 * second factor). A client that stored this as a session would store nothing.
 */
export interface PasswordChangeChallenge {
  passwordChangeRequired: true
  changeToken: string
  expiresInSeconds: number
}

export type StaffLoginResult =
  | StaffTokens
  | MfaChallenge
  | MfaEnrolmentChallenge
  | PasswordChangeChallenge

export const isMfaChallenge = (result: StaffLoginResult): result is MfaChallenge =>
  'mfaRequired' in result

export const isPasswordChangeChallenge = (
  result: StaffLoginResult,
): result is PasswordChangeChallenge => 'passwordChangeRequired' in result

export const isMfaEnrolmentChallenge = (
  result: StaffLoginResult,
): result is MfaEnrolmentChallenge => 'mfaEnrolmentRequired' in result

/**
 * `POST /auth/mfa/enrol/start` — the compulsory first enrolment, before a
 * session exists.
 *
 * 🔴 PUBLIC, AND THE TOKEN IS THE AUTHENTICATION — `claimInvite`'s shape. The
 * operator has no session and cannot get one until this completes.
 */
export const startForcedMfaEnrolment = (enrolmentToken: string): Promise<MfaEnrolment> =>
  request<MfaEnrolment>('/auth/mfa/enrol/start', {
    method: 'POST',
    context: 'public',
    body: { enrolmentToken },
  })

/**
 * `POST /auth/mfa/enrol/finish` — turn it on, and sign in.
 *
 * 🔴 IT RETURNS THE RECOVERY CODES **AND** THE SESSION. Both factors have now
 * been presented: a correct password (the token is the proof) and a working
 * code. The codes come back once and only their hashes are stored — and on this
 * path the operator has just made a second factor compulsory for themselves, so
 * losing them is the difference between a lost phone and a lost platform.
 */
export const finishForcedMfaEnrolment = (
  enrolmentToken: string,
  code: string,
): Promise<StaffTokens & { recoveryCodes: string[] }> =>
  request('/auth/mfa/enrol/finish', {
    method: 'POST',
    context: 'public',
    body: { enrolmentToken, code },
  })

/**
 * Which sign-in screen a login came from.
 *
 * ⚠️ STRUCTURALLY THE SAME UNION AS `Door` IN `app/router/routes.ts`, AND
 * DECLARED SEPARATELY ON PURPOSE. `shared/` must not import from `app/` — the
 * dependency runs the other way — and this is the wire contract rather than the
 * router's vocabulary. The two are checked against each other by the call site,
 * which passes one as the other.
 */
export type StaffDoor = 'teacher' | 'school' | 'operator'

/**
 * @param door which door this was typed at. The server refuses a correct
 * password whose account belongs to another tier, with the same generic 401 as
 * a wrong one — so an operator at the teacher door is turned away at the
 * password step instead of being walked through TOTP first.
 *
 * ⚠️ IT ASSERTS NOTHING AND GRANTS NOTHING. Naming a door can only narrow what
 * this call will accept; `mayNavigate` and the server's `@Roles` guards are
 * still the only things deciding what an account may reach.
 */
export function staffLogin(
  email: string,
  password: string,
  door: StaffDoor,
  /**
   * P0-13 / D9 — the "Keep me signed in" box. `false` asks the server for a
   * 12-hour session that rotation never extends; `true` for the thirty days.
   * Omitted (the operator door, which has no box) the server decides — and it
   * caps every operator session at 12 hours whatever is sent.
   */
  remember?: boolean,
): Promise<StaffLoginResult> {
  return request<StaffLoginResult>('/auth/login', {
    method: 'POST',
    context: 'public',
    body: remember === undefined ? { email, password, door } : { email, password, door, remember },
  })
}

/** The signed-in account. `name` is null on a row that never got one. */
export interface StaffIdentity {
  userId: string
  name: string | null
  email: string | null
  role: string
}

/**
 * `GET /auth/me` — who is holding this token.
 *
 * 🔴 THE ONE ANSWER THE CLIENT COULD NOT DERIVE AND ONE SCREEN GUESSED. Sign-in
 * returns a token pair, and the token deliberately carries no name (`jwt.ts`:
 * *"a claim carrying a person's name would be a claim"*) — so the teacher tier's
 * own greeting had no source for the name in it and fell back to the name of
 * whoever runs the class on screen. That was always the same person until E4 let
 * a subject teacher open a class they do not run, at which point the product
 * began greeting people by a colleague's name.
 *
 * ⚠️ IT TAKES NO ID. The route answers about the caller and nothing else; one
 * that took a `userId` would be a staff directory readable with any token.
 */
export const getStaffIdentity = (auth: StaffAuth = {}): Promise<StaffIdentity> =>
  request<StaffIdentity>('/auth/me', {
    context: 'user',
    token: auth.token,
    signal: auth.signal,
  })

/**
 * `POST /auth/login/mfa` — the second half of a challenged sign-in.
 *
 * 🔴 `code` TAKES A 30-SECOND AUTHENTICATOR CODE **OR** A RECOVERY CODE, in one
 * field, and the screen must offer one box rather than two. Somebody reaching
 * for a recovery code is by definition unable to produce an authenticator one —
 * asking them to first classify their own problem is the moment they are least
 * able to. The server tells the two apart by shape.
 *
 * ⚠️ ONE GENERIC 401 FOR AN EXPIRED CHALLENGE, A WRONG CODE, A CODE ALREADY USED
 * AND A SPENT RECOVERY CODE. `client.ts` discards the body, so the only honest
 * sentence names the two things a person can act on: the code may be wrong, or
 * the sign-in may have taken too long and need starting again.
 *
 * ⚠️ THE PASSWORD IS NOT RE-SENT. Holding a plaintext credential in a component
 * across a user interaction — while somebody unlocks a phone, opens an app and
 * reads six digits — is strictly worse than a five-minute assertion that grants
 * nothing on its own.
 */
export function completeStaffMfa(
  mfaToken: string,
  code: string,
  /** The box as it was ticked on the password step — see `staffLogin`. */
  remember?: boolean,
): Promise<StaffTokens> {
  return request<StaffTokens>('/auth/login/mfa', {
    method: 'POST',
    context: 'public',
    body: remember === undefined ? { mfaToken, code } : { mfaToken, code, remember },
  })
}

/**
 * `POST /auth/password/change-required` — the step `PasswordChangeChallenge`
 * opens. Public: the change token is the authentication, and it proves only
 * that the password somebody else chose was typed correctly a moment ago.
 *
 * ⚠️ IT ANSWERS WITH THE REST OF THE SIGN-IN, not a bare success. An account
 * with a second factor still has to present it, so the result is the same union
 * `/auth/login` returns (minus a second change, which cannot happen).
 */
export function completeRequiredPasswordChange(
  changeToken: string,
  newPassword: string,
  remember?: boolean,
): Promise<StaffLoginResult> {
  return request<StaffLoginResult>('/auth/password/change-required', {
    method: 'POST',
    context: 'public',
    body:
      remember === undefined
        ? { changeToken, newPassword }
        : { changeToken, newPassword, remember },
  })
}

/* ────────────────────────────────────── P3-16 · forgot / reset password ── */

/**
 * `POST /auth/password/forgot` — ask for a reset link.
 *
 * 🔴 ALWAYS THE SAME 200, WHETHER OR NOT THE ADDRESS HAS A LOGIN. A form that
 * said "no such account" would be a directory of a school's staff, so the
 * screen has exactly one thing to say afterwards and must not branch on the
 * body. Operators get no mail from this — their recovery is another operator.
 */
export function requestPasswordReset(email: string): Promise<{ sent: true }> {
  return request<{ sent: true }>('/auth/password/forgot', {
    method: 'POST',
    context: 'public',
    body: { email },
  })
}

/** What a used reset link tells the person who used it: whose account, so which door. */
export interface PasswordResetDone {
  reset: true
  email: string | null
  role: Role
}

/**
 * `POST /auth/password/reset` — spend a single-use link on a new password.
 *
 * ⚠️ ONE GENERIC 403 FOR UNKNOWN, EXPIRED, USED AND REVOKED — `claimInvite`'s
 * rule, for its reason. A 400 is the password rule. Every other session of the
 * account is signed out server-side on success.
 */
export function resetPassword(token: string, password: string): Promise<PasswordResetDone> {
  return request<PasswordResetDone>('/auth/password/reset', {
    method: 'POST',
    context: 'public',
    body: { token, password },
  })
}

/* ──────────────────────────────────────────── P2-02 · invite preview ── */

/**
 * `POST /auth/invite/preview` — who a link is for, so the set-password page can
 * greet them (Figma P4: "Welcome to Kalqy, Greenwood").
 *
 * 🔴 THE SAME SHAPE FOR A DEAD LINK, WITH EVERY FIELD NULL. Unknown, expired,
 * revoked and claimed are one answer, as they are on claim — so the screen
 * branches on `valid` alone and can learn nothing else about a token it does
 * not hold. Anyone who does hold a live token could already learn as much by
 * claiming it, which is why this is not a new oracle.
 */
export interface InvitePreview {
  valid: boolean
  purpose: 'INVITE' | 'PASSWORD_RESET' | null
  email: string | null
  name: string | null
  role: 'ADMIN' | 'TEACHER' | 'COORDINATOR' | 'PRINCIPAL' | 'SUPER_ADMIN' | null
  schoolName: string | null
  /** The school's child seat limit; null when uncapped or there is no plan. */
  children: number | null
  /** The teacher seat limit; null when none. */
  teachers: number | null
  planName: string | null
  /** ISO instant. */
  expiresAt: string | null
}

export function previewInvite(token: string): Promise<InvitePreview> {
  return request<InvitePreview>('/auth/invite/preview', {
    method: 'POST',
    context: 'public',
    body: { token },
  })
}

/* ───────────────────────────────── the second factor, self-service ── */

export interface MfaStatus {
  /** A secret exists. Says nothing about whether it works. */
  enrolled: boolean
  /** A code has been proved. This is the one the sign-in path reads. */
  confirmed: boolean
  confirmedAt: string | null
  /** 🔴 Counted, never listed — after issue only the hashes exist. */
  recoveryCodesRemaining: number
  /** Whether this account would be refused a sign-in without one. */
  requiredForThisAccount: boolean
  /**
   * False when the deployment has no `TOTP_ENC_KEY`. D4's rule: the screen must
   * not offer a control that cannot work — no enrolment button, and a sentence
   * saying why.
   */
  available: boolean
}

export interface MfaEnrolment {
  /** Base32, in four-character groups, for the "enter a setup key" path. */
  secret: string
  otpauthUri: string
  /**
   * 🔴 AN INLINE SVG, GENERATED SERVER-SIDE, NOT A URL TO A QR SERVICE. Every
   * hosted QR generator works by being sent the payload — which here is a live
   * TOTP secret. The whole second factor would be handed to a third party as a
   * query string, in exchange for saving an encoder.
   */
  qrSvg: string
  periodSeconds: number
}

export const getMfaStatus = (token: string): Promise<MfaStatus> =>
  request<MfaStatus>('/auth/mfa/status', { context: 'user', token })

/**
 * Step one. Mints a secret and returns it once — and changes **nothing** about
 * signing in until `confirmMfaSetup` succeeds.
 *
 * 🔴 THAT ORDERING IS THE FEATURE, NOT AN IMPLEMENTATION DETAIL. Turning the
 * factor on before the phone has proved it holds the secret is how an operator
 * ends up locked out of a platform whose own sign-in screen says there is no
 * self-service recovery.
 */
export const beginMfaSetup = (token: string): Promise<MfaEnrolment> =>
  request<MfaEnrolment>('/auth/mfa/setup', { method: 'POST', context: 'user', token })

/**
 * Step two. The first correct code turns it on and returns the recovery codes.
 *
 * 🔴 THE RECOVERY CODES COME BACK EXACTLY ONCE. Only their hashes are stored, so
 * this response body is the only copy that will ever exist — the screen must say
 * so before the panel can be closed, exactly as the school-admin credential
 * panel does.
 */
export const confirmMfaSetup = (
  code: string,
  token: string,
): Promise<{ confirmedAt: string; recoveryCodes: string[] }> =>
  request('/auth/mfa/confirm', { method: 'POST', context: 'user', token, body: { code } })

/**
 * ⚠️ IT ASKS FOR A CODE, like `changeOwnPassword` asks for the current password.
 * A session alone is a laptop left open in a staff room; turning the factor off
 * with one makes it worth exactly as much as the session it exists to be
 * independent of.
 */
export const disableMfa = (code: string, token: string): Promise<{ disabled: true }> =>
  request('/auth/mfa/disable', { method: 'POST', context: 'user', token, body: { code } })

/** Fresh codes, invalidating every previous one. Needs a current code. */
export const regenerateMfaRecoveryCodes = (
  code: string,
  token: string,
): Promise<{ recoveryCodes: string[] }> =>
  request('/auth/mfa/recovery-codes', { method: 'POST', context: 'user', token, body: { code } })

/* ────────────────────────────────────── §9.1 · parent phone OTP ── */

/** What the request route answers with. `devCode` only ever under OTP_DEV_ECHO. */
export interface OtpRequestResult {
  message: string
  /**
   * 🔴 LOCAL DEVELOPMENT ONLY, AND THE SERVER REFUSES TO BOOT IF IT IS SET IN
   * PRODUCTION (`env.validation.ts`). It is typed because the field is real and
   * pretending otherwise would mean casting it in whatever screen wants it —
   * never rendered on a screen a parent can reach.
   */
  devCode?: string
}

/**
 * `POST /auth/parent/otp/request` — *"always generic 200"*.
 *
 * 🔴 THE CALLER MUST NOT BRANCH ON THE ANSWER, AND THERE IS NOTHING TO BRANCH
 * ON. The server replies identically for a registered number, a stranger's and
 * gibberish, because these numbers belong to parents of identified children and
 * a form that distinguishes them is a phone-enumeration oracle (threat T4). A
 * screen that only advanced to the code step on "success" would rebuild that
 * oracle in the UI out of an endpoint carefully built to prevent it.
 *
 * ⚠️ IT CAN STILL ANSWER 429 — five a minute per IP, so a family sharing a
 * connection can meet it. That is about the request, not about the number, and
 * it needs a different sentence from "check the number".
 */
export function requestParentOtp(phone: string): Promise<OtpRequestResult> {
  return request<OtpRequestResult>('/auth/parent/otp/request', {
    method: 'POST',
    context: 'public',
    body: { phone },
  })
}

/**
 * `POST /auth/parent/otp/verify` — one generic 401 for every refusal.
 *
 * ⚠️ A WRONG CODE, AN EXPIRED CHALLENGE, A CAPPED ONE AND AN UNKNOWN NUMBER ARE
 * ONE ANSWER. `client.ts` discards the body, so the caller has a status and
 * nothing else — which is exactly as much as it should have. The honest
 * sentence names the two things a parent can act on: the code may be wrong, or
 * it may have aged out.
 */
export function verifyParentOtp(phone: string, code: string): Promise<ParentTokens> {
  return request<ParentTokens>('/auth/parent/otp/verify', {
    method: 'POST',
    context: 'public',
    body: { phone, code },
  })
}

/**
 * `POST /auth/refresh` — single-use rotation (the presented `jti` is deleted).
 *
 * ⚠️ NOTHING CALLS THIS YET, and that is the honest state rather than dead code.
 * `JWT_ACCESS_TTL` is 15 minutes and `staffSession.ts` deliberately holds the
 * refresh token, so the rotation this needs is a scheduler decision — one an
 * offline-capable PWA has to make carefully — rather than a missing endpoint.
 * Discarding it here would mean the day that scheduler is written, every
 * signed-in staff member has nothing to rotate with. 📋 Reported.
 */
export function refreshStaffSession(refreshToken: string): Promise<StaffTokens> {
  return request<StaffTokens>('/auth/refresh', {
    method: 'POST',
    context: 'public',
    body: { refreshToken },
  })
}

/**
 * `POST /auth/logout` — revoke the refresh token server-side.
 *
 * 🔴 IT RETURNS `void` AND SWALLOWS EVERY FAILURE, BY DESIGN. The local half of
 * signing out (`clearStaffSession`) is unconditional and happens first; this is
 * the remote half. A sign-out that reported an error would invite a screen to
 * keep the session open on failure — and the failure mode there is the previous
 * teacher's dashboard, still signed in, on a laptop the next class is about to
 * use.
 *
 * ⚠️ Public and idempotent server-side: it takes the refresh token in the body
 * rather than reading the access token, so a session whose 15-minute access
 * token already expired can still be ended.
 */
/**
 * OD-100 — claim a staff invitation and choose a password.
 *
 * 🔴 PUBLIC BY NECESSITY, AND THE TOKEN IS THE AUTHENTICATION. The invitee has
 * no account they can sign into yet; that is what the invitation is for. Which
 * is why the token is 256 bits, single-use and short-lived.
 *
 * 🔴 THIS FUNCTION EXISTING IS THE POINT. `POST /auth/invite` and
 * `/auth/invite/claim` shipped tested and audited, and **nothing in the frontend
 * called either** — so every invitation the product could issue was a link
 * pointing at a screen that did not exist. That stranded E3's teacher
 * provisioning and the operator-recovery path at the same time.
 *
 * ⚠️ ONE GENERIC REFUSAL FOR UNKNOWN, EXPIRED, REVOKED AND ALREADY-CLAIMED, and
 * the client must not try to tell them apart. Distinguishing them would let
 * somebody with a half-remembered link learn whether it ever existed, and tell
 * an attacker holding a claimed token that they were one step too late.
 *
 * ✅ A SUCCESSFUL CLAIM SAYS WHOSE ACCOUNT IT WAS — the email and role — so the
 * screen sends the person to their own sign-in door instead of guessing one.
 */
export interface ClaimedInvite {
  claimed: true
  email: string | null
  role: Role
  /**
   * ✅ P2-02 — SIGNED IN ON CLAIM, when asked and when the role allows it. A new
   * school admin used to set a password and then type it again at the sign-in
   * door before reaching "Pay". Never present for an operator (the second factor
   * comes first) and absent from an older server. It is a SESSION-ONLY session
   * (12 h, this tab) — the person never saw a "Keep me signed in" box.
   */
  session?: StaffTokens | null
}

export function claimInvite(
  token: string,
  password: string,
  options: { signIn?: boolean } = {},
): Promise<ClaimedInvite> {
  return request<ClaimedInvite>('/auth/invite/claim', {
    method: 'POST',
    context: 'public',
    body: options.signIn ? { token, password, signIn: true } : { token, password },
  })
}

/**
 * G1 — change your own password.
 *
 * 🔴 THE CURRENT PASSWORD IS REQUIRED AND THE TYPE SAYS SO. An access token
 * alone is a laptop left open in a staff room, not proof of presence.
 *
 * ⚠️ IT SIGNS EVERY OTHER SESSION OUT, including this tab's refresh token — the
 * caller has to decide what to do next, which is why the count comes back.
 */
export function changeOwnPassword(
  currentPassword: string,
  newPassword: string,
  token: string,
): Promise<{ revokedSessions: number }> {
  return request<{ revokedSessions: number }>('/auth/password', {
    method: 'POST',
    context: 'user',
    token,
    body: { currentPassword, newPassword },
  })
}

export async function endStaffSession(refreshToken: string): Promise<void> {
  try {
    await request<void>('/auth/logout', {
      method: 'POST',
      context: 'public',
      body: { refreshToken },
    })
  } catch {
    // Offline, 4xx, anything. The credential is already gone from this tab, and
    // the token expires on its own; there is nothing useful to tell the user.
  }
}

/* ─────────────────────────────────────────── change 19 §6 · the terms ── */

export interface TermsSection {
  heading: string
  body: string
}

/**
 * What the signed-in person has agreed to, and whether they are behind the gate.
 *
 * 🔴 `required` ANSWERS *"WILL THIS PERSON BE STOPPED"*, NOT *"IS THERE A ROW"*.
 * The server computes it from the role as well as the acceptance, so it is false
 * for every role `TermsGuard` does not gate. A client that recomputed it from
 * `acceptedAt === null` would put a blocking screen in front of a teacher whose
 * head signs on their behalf.
 */
export interface TermsStatus {
  version: string
  sections: TermsSection[]
  required: boolean
  acceptedAt: string | null
  /** What they last agreed to, when that is not the current version. */
  acceptedVersion: string | null
}

export const getTerms = (auth: { token?: string; signal?: AbortSignal } = {}): Promise<TermsStatus> =>
  request<TermsStatus>('/auth/terms', {
    context: 'user',
    token: auth.token,
    signal: auth.signal,
  })

/**
 * ⚠️ NO BODY. The version and the moment are both the server's — a browser that
 * could name the version it accepted could claim to have agreed to a document it
 * was never shown.
 */
export const acceptTerms = (auth: { token?: string } = {}): Promise<TermsStatus> =>
  request<TermsStatus>('/auth/terms/accept', {
    method: 'POST',
    body: {},
    context: 'user',
    token: auth.token,
  })
